import * as Sentry from '@sentry/react'
import ActBtn from 'components/buttons/ActBtn'
import Btn from 'components/buttons/Btn'
import I from 'components/I'
import SpinnerFull from 'components/SpinnerFull'
import PropTypes from 'prop-types'
import { Button, Col, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import API from 'state/API'
import { PaymentDetails } from 'state/models/Order'
import { grabPaymentInfo } from 'state/Orders'
import { Alerts, JWTCheckError, storeRefs, useBoolState, useSimpleState } from 'utils'

export default function CardInfoModal({ show, doClose }) {
	const { t }                  = useTranslation()
	const { id }                 = useParams()
	const dispatch               = useDispatch()
	const [refs, storage, value] = storeRefs()
	const card                   = useSimpleState(false)
	const loadingCardInfo        = useBoolState()
	const pwdVisible             = useBoolState()

	/**
	 * @param preFetch {boolean} Avoids spamming errors of invalid password if there's some mismatch with the timeout
	 * @returns {Promise<void>}
	 */
	async function grabCardInfo(preFetch) {
		if (!preFetch && !value('password')) {
			Alerts.danger(t('The password field is empty.'))
			return
		}

		loadingCardInfo.on()
		await dispatch(grabPaymentInfo(id, preFetch? null : value('password')))
			.then(resp => {
				card.set(resp.data? new PaymentDetails(resp.data) : false) //receiving false means no info was gathered
			})
			.catch(e => {
				let msg = false
				if (e instanceof JWTCheckError) {
					if (preFetch) {
						return false
					} else {
						console.error('Uh... we got a JWT Check error during a non-prefetch of card details??')
						Sentry.captureException(e, { extra: { description: 'JWT check error during non-prefetch' }})
						msg = e.message
					}
				} else {
					switch (e.response.status) { // eslint-disable-line default-case
						case 402: msg = t('That password was incorrect. Try again.'); break
						case 410: msg = t('There are no more payment details about this order.')+' '+t('CONTACT_ERROR'); break
						case 429:
							const timeout = e.response.data.error.message.match(/(\d+ \w+)/)
							msg = t('You have entered too many incorrect passwords. Please try again in {{time}}.', { time: timeout[1] })
							break
					}
				}

				if (!preFetch && msg) {
					Alerts.danger(msg)
				}
			})
			.finally(loadingCardInfo.off)
	}

	async function doSubmit() {
		grabCardInfo(false)
	}

	function onShow() {
		refs.password?.focus() //the ref might not exist if the user is under the password grant period
		grabCardInfo(true)
	}

	function onHide() {
		card.set(false) //forces to clean card data, so every time the popup is opened, it needs to check with the server for credit card access / API token validity
		doClose()
	}

	return <Modal {...{show, onHide, onShow}} id="card-info" className="d-print-none">
		<Form onSubmit={doSubmit}>
			<Modal.Header closeButton>
				<Modal.Title>{t('Complete payment information')}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{loadingCardInfo.is?
					<SpinnerFull icon="key" resource={API.RESOURCES.ORDERS}/>
				:
					(card.val?
						<>
							{'number' in card.val &&
								<Table size="sm" className="mb-0">
									<tbody>
										<tr>
											<th>
												<span className="d-none d-xs-block">{t('Card number')}</span>
												<span className="d-xs-none d-xxs-block">{t('Number')}</span>
											</th>
											<td><tt>{card.val.number}</tt></td>
										</tr>
										<tr><th>{t('Expires')}</th><td><tt>{card.val.expires}</tt></td></tr>
										{card.val.ccv && <tr><th>{t('CCV')}</th><td>{card.val.ccv}</td></tr>}
										<tr><th>{t('Name', { context: 'credit card'})}</th><td>{card.val.name}</td></tr>
										<tr><th>{t('ZIP')}</th><td>{card.val.zip}</td></tr>
									</tbody>
								</Table>
							}
							{card.val.disclaimer &&
								<small><I force><b>{t('Disclaimer:')}</b> {card.val.disclaimer}</I></small>}
						</>
						:
							<Form.Group controlId="password">
								<Form.Label>{t('Enter your credit cards password:')}</Form.Label>
								<InputGroup>
									<Form.Control type={pwdVisible.is? 'text' : 'password'} ref={storage} autoComplete="one-time-code" tabIndex={1}/>
									<InputGroup.Append>
										<Btn variant="outline-dark" onClick={() => pwdVisible.toggle(pwdVisible.isnt)} tabIndex={2}
										     icon={pwdVisible.is? 'eye-slash' : 'eye'} fixedWidth
									       title={pwdVisible.is? t('Hide password') : t('See password')}/>
									</InputGroup.Append>
								</InputGroup>
								<Form.Text className="text-muted">{t('Also known as PDFs password.')}</Form.Text>
							</Form.Group>
					)
				}
			</Modal.Body>

			{loadingCardInfo.isnt && <Modal.Footer as={Row}>
				<Col xs="auto">
					<Button onClick={onHide} variant={card.val? 'secondary' : 'outline'} size="sm" tabIndex={4}>
						{card.val? t('Close') : t('Cancel', { context: 'go back' })}
					</Button>
				</Col>
				<Col>
					<ActBtn type="submit" block variant="success" icon="credit-card" className={card.val? 'd-none' : ''}
					        action={doSubmit} texts={[t('Show details'), t('Checking'), '']} tabIndex={3}/>
				</Col>
			</Modal.Footer>}
		</Form>
	</Modal>
}

CardInfoModal.propTypes = {
	show   : PropTypes.bool.isRequired,
	doClose: PropTypes.func.isRequired,
}
